import { Badge } from "@/modules/shadcn/ui/badge";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Switch } from "@/modules/shadcn/ui/switch";
import {
  FEATURE_FLAG_DEFS,
  FeatureFlagDef,
  FeatureFlagValue,
  Organization,
  pruneFeatureFlags,
  resolveFeatureFlagsRecord,
  StoredFeatureFlags,
} from "@repo/schema";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateFeatureFlagsMutation } from "../../services";

export interface FeatureFlagsSectionProps {
  organization: Organization;
}

/**
 * The draft value for a flag, falling back to its declared default only when the
 * key is genuinely absent. `??` would be wrong here: `null` is a legal value for a
 * nullable flag (e.g. "Unlimited"), and coalescing would silently replace it.
 */
const draftValue = (draft: StoredFeatureFlags, def: FeatureFlagDef): FeatureFlagValue =>
  def.key in draft ? (draft[def.key] as FeatureFlagValue) : def.default;

/**
 * Super-admin-only editor for the current organization's feature flags.
 *
 * Rows are rendered from the code-declared registry in `@repo/schema`, so adding
 * a flag needs no change here. Only values differing from their declared default
 * are persisted — setting a control back to its default is the reset gesture.
 */
export const FeatureFlagsSection = ({ organization }: FeatureFlagsSectionProps) => {
  const { t } = useTranslation(["account", "common"]);
  const [updateFeatureFlags, { isLoading: isSaving }] = useUpdateFeatureFlagsMutation();

  const effective = useMemo(
    () => resolveFeatureFlagsRecord(organization.featureFlags),
    [organization.featureFlags],
  );
  const [draft, setDraft] = useState<StoredFeatureFlags>(effective);

  useEffect(() => {
    setDraft(effective);
  }, [effective]);

  const isDirty = FEATURE_FLAG_DEFS.some((def) => draft[def.key] !== effective[def.key]);

  const setFlag = (key: string, value: FeatureFlagValue) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const handleSave = async () => {
    try {
      await updateFeatureFlags(pruneFeatureFlags(draft)).unwrap();
      toast.success(t("featureFlags.updated"));
    } catch {
      toast.error(t("featureFlags.updateFailed"));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h5 className="text-xl font-semibold">{t("featureFlags.title")}</h5>
        <Badge variant="secondary">{t("featureFlags.superAdmin")}</Badge>
      </div>

      {FEATURE_FLAG_DEFS.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Trans
            t={t}
            i18nKey="featureFlags.empty"
            components={{ code: <code className="rounded bg-black/5 px-1 py-0.5" /> }}
          />
        </p>
      ) : (
        <>
          <div className="flex flex-col divide-y divide-black/10">
            {FEATURE_FLAG_DEFS.map((def) => (
              <FeatureFlagRow
                key={def.key}
                def={def}
                value={draftValue(draft, def)}
                isOverridden={draftValue(draft, def) !== def.default}
                onChange={(value) => setFlag(def.key, value)}
              />
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isSaving || !isDirty}>
              {isSaving ? t("featureFlags.saving") : t("featureFlags.save")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

interface FeatureFlagRowProps {
  def: FeatureFlagDef;
  value: FeatureFlagValue;
  isOverridden: boolean;
  onChange: (value: FeatureFlagValue) => void;
}

const FeatureFlagRow = ({ def, value, isOverridden, onChange }: FeatureFlagRowProps) => {
  const { t } = useTranslation("account");
  const inputId = `feature-flag-${def.key}`;

  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Label htmlFor={inputId}>{def.label}</Label>
          {isOverridden && (
            <Badge variant="secondary" className="font-normal">
              {t("featureFlags.overridden")}
            </Badge>
          )}
        </div>
        {def.description && (
          <span className="text-sm text-muted-foreground">{def.description}</span>
        )}
      </div>
      <div className="shrink-0">
        <FeatureFlagControl
          def={def}
          inputId={inputId}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

interface FeatureFlagControlProps extends FeatureFlagRowProps {
  inputId: string;
}

const FeatureFlagControl = ({
  def,
  inputId,
  value,
  onChange,
}: Omit<FeatureFlagControlProps, "isOverridden">) => {
  switch (def.type) {
    case "boolean":
      return (
        <Switch
          id={inputId}
          checked={typeof value === "boolean" ? value : def.default}
          onCheckedChange={onChange}
        />
      );
    case "number":
      return (
        <NumberInput
          id={inputId}
          className="w-32"
          // An empty field is the null state for a nullable flag; for the rest it
          // is a transient mid-edit state that snaps back to the default.
          value={typeof value === "number" ? value : def.nullable ? null : def.default}
          min={def.min}
          max={def.max}
          placeholder={def.nullable === true ? def.nullLabel : undefined}
          onValueChange={(next) =>
            onChange(next === null && def.nullable !== true ? def.default : next)
          }
        />
      );
    case "string":
      return (
        <Input
          id={inputId}
          className="w-56"
          value={typeof value === "string" ? value : def.default}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "enum": {
      const selected = typeof value === "string" ? value : def.default;
      // The trigger otherwise shows the raw value ("deployment"), not its label.
      const selectedLabel = def.options.find((option) => option.value === selected)?.label;
      return (
        <Select value={selected} onValueChange={(next) => next && onChange(next)}>
          <SelectTrigger id={inputId} className="w-56">
            <SelectValue placeholder={def.label}>{selectedLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {def.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
  }
};
